import { compareInstants } from '../format/when';
import type { PullRequestSummary } from '../api/types';
import {
  ladderDetail,
  ladderReach,
  type ReleaseLadder,
} from './releaseChannelsModel';

/**
 * A PR's position on its path to `main` and out to the fleet, left to right.
 * Every stage but `released` is derived from the summary alone, so the track
 * costs no extra requests; `released` needs the repository's release ladder
 * (see `releaseChannelsModel`) and reads `unknown` without it.
 */
export type PullStageId =
  | 'opened'
  | 'checks'
  | 'review'
  | 'mergeable'
  | 'merged'
  | 'released';

/**
 * done: passed; active: in progress now; blocked: stopped here; pending: not
 * reached; skipped: never will be; unknown: cannot be decided from what the
 * server said (a capped compare, a release the forge does not record).
 */
export type PullStageStatus = 'done' | 'active' | 'blocked' | 'pending' | 'skipped' | 'unknown';

export interface PullStage {
  id: PullStageId;
  label: string;
  status: PullStageStatus;
  detail: string;
}

export const PULL_STAGE_LABELS: Record<PullStageId, string> = {
  opened: 'Opened',
  checks: 'Checks',
  review: 'Review',
  mergeable: 'Mergeable',
  merged: 'Merged',
  released: 'Released',
};

export function pullStages(pr: PullRequestSummary, ladder?: ReleaseLadder): PullStage[] {
  const stage = (id: PullStageId, status: PullStageStatus, detail: string): PullStage => ({
    id,
    label: PULL_STAGE_LABELS[id],
    status,
    detail,
  });

  // A merged PR still reports how it got there: a merge over red checks or
  // with no recorded approval stays visible instead of turning green.
  if (pr.state === 'merged') {
    const settled = (status: PullStageStatus): PullStageStatus =>
      status === 'pending' || status === 'active' ? 'skipped' : status;
    // Past the merge, "open" and "mergeable" are history, not status: they are
    // simply passed. Checks keep their verdict, which `checksDetail` already
    // qualifies as "not required" for the red ones that did not stop the merge.
    return [
      stage('opened', 'done', ''),
      stage('checks', settled(checksStage(pr)), checksDetail(pr)),
      stage('review', settled(reviewStage(pr)), reviewDetail(pr)),
      stage('mergeable', 'done', ''),
      stage('merged', 'done', 'merged'),
      releaseStage(pr, ladder),
    ];
  }

  const checks = checksStage(pr);
  const review = reviewStage(pr);
  const closed = pr.state === 'closed';
  const mergeable: PullStageStatus = closed
    ? 'skipped'
    : pr.mergeable.can_merge
      ? 'done'
      : checks === 'done' && review === 'done'
        ? 'blocked'
        : 'pending';

  return [
    stage('opened', pr.draft ? 'active' : 'done', pr.draft ? 'draft' : 'open'),
    stage('checks', closed && checks !== 'done' ? 'skipped' : checks, checksDetail(pr)),
    stage('review', closed && review !== 'done' ? 'skipped' : review, reviewDetail(pr)),
    stage('mergeable', mergeable, pr.mergeable.reason ?? pr.mergeable.level),
    stage('merged', closed ? 'blocked' : 'pending', closed ? 'closed unmerged' : 'not yet'),
    releaseStage(pr, ladder),
  ];
}

/**
 * How far out the change has shipped. Without a ladder the stage says
 * `unknown` for a merged PR — the page has not been told where it is, which is
 * not the same as it being unreleased.
 */
function releaseStage(pr: PullRequestSummary, ladder?: ReleaseLadder): PullStage {
  const stage = (status: PullStageStatus, detail: string): PullStage => ({
    id: 'released',
    label: PULL_STAGE_LABELS.released,
    status,
    detail,
  });
  if (pr.state === 'closed') return stage('skipped', 'not merged');
  if (!ladder) return stage(pr.state === 'merged' ? 'unknown' : 'pending', releasePending(pr));
  // No deployment and no tag anywhere: nothing will ever mark this released.
  if (ladder.kind === 'none') {
    return stage(pr.state === 'merged' ? 'skipped' : 'pending', ladderDetail(ladder, pr.state));
  }
  if (pr.state !== 'merged') return stage('pending', ladderDetail(ladder, pr.state));
  return stage(ladderReach(ladder), ladderDetail(ladder, pr.state));
}

function releasePending(pr: PullRequestSummary): string {
  return pr.state === 'merged' ? 'release unknown' : 'not yet';
}

/**
 * Red checks that did not stop the merge: the forge says the pull request can
 * merge, or it has merged. Those failures are on checks the base branch does
 * not require (`jankurai/proof`, a mirror), so they read qualified, not red.
 */
function failuresNotRequired(pr: PullRequestSummary): boolean {
  return pr.checks.failing > 0 && (pr.state === 'merged' || pr.mergeable.can_merge);
}

function checksStage(pr: PullRequestSummary): PullStageStatus {
  const { total, failing, pending } = pr.checks;
  if (total === 0) return 'pending';
  if (failing > 0 && !failuresNotRequired(pr)) return 'blocked';
  if (pending > 0) return 'active';
  return 'done';
}

function reviewStage(pr: PullRequestSummary): PullStageStatus {
  const { approvals, required_approvals, changes_requested } = pr.review;
  if (changes_requested > 0) return 'blocked';
  if (approvals >= Math.max(required_approvals, 1)) return 'done';
  return approvals > 0 ? 'active' : 'pending';
}

function checksDetail(pr: PullRequestSummary): string {
  const { total, passing, failing, pending } = pr.checks;
  if (total === 0) return 'no checks';
  if (failing > 0) {
    return failuresNotRequired(pr) ? `${failing} failing, not required` : `${failing} failing`;
  }
  if (pending > 0) return `${pending} running`;
  return `${passing}/${total} passing`;
}

function reviewDetail(pr: PullRequestSummary): string {
  const { approvals, required_approvals, changes_requested } = pr.review;
  if (changes_requested > 0) return 'changes requested';
  return `${approvals}/${Math.max(required_approvals, 1)} approvals`;
}

/** Open PRs first, then most recently updated. */
export function timelineOrder(a: PullRequestSummary, b: PullRequestSummary): number {
  const rank = (pr: PullRequestSummary) => (pr.state === 'open' ? 0 : 1);
  return rank(a) - rank(b) || compareInstants(b.updated_at, a.updated_at) || b.number - a.number;
}

export interface PullCounts {
  open: number;
  waiting: number;
  blocked: number;
}

/**
 * The page in one line: how many are open, how many wait on checks, how many
 * are stopped by a red check, and — when the release ladder has loaded — how
 * many have merged but not shipped yet. The counts come from whoever knows
 * them in full: the server's snapshot summary for the whole forge, or the
 * loaded rows when the page is scoped to a family or a repository.
 */
export function pullCountsSentence(counts: PullCounts, awaitingRelease?: number): string {
  const parts = [
    `${counts.open} open`,
    `${counts.waiting} waiting on checks`,
    `${counts.blocked} stopped by a failing check`,
  ];
  if (awaitingRelease !== undefined && awaitingRelease > 0) {
    parts.push(`${awaitingRelease} merged, not yet released`);
  }
  return parts.join(' · ');
}

/**
 * The same counts read off a list of pull requests this view holds in full. A
 * red check that does not stop the merge (the forge says the pull request can
 * merge) is not counted as blocking.
 */
export function pullCountsOf(pulls: PullRequestSummary[]): PullCounts {
  const open = pulls.filter((pr) => pr.state !== 'merged' && pr.state !== 'closed');
  return {
    open: open.length,
    waiting: open.filter(
      (pr) => pr.checks.failing === 0 && (pr.checks.total === 0 || pr.checks.pending > 0)
    ).length,
    blocked: open.filter((pr) => pr.checks.failing > 0 && !pr.mergeable.can_merge).length,
  };
}

export function timelineSentence(pulls: PullRequestSummary[], awaitingRelease?: number): string {
  return pullCountsSentence(pullCountsOf(pulls), awaitingRelease);
}
